import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Article } from '../../types';
import { useCollection } from '../../hooks/useCollection';
import { doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import ConfirmModal from '../../components/ConfirmModal';
import { useToast } from '../../hooks/useToast';

const PlusIcon: React.FC<{className?: string}> = ({className}) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
);

const ToggleSwitch: React.FC<{ enabled: boolean, onChange: () => void, srText: string }> = ({ enabled, onChange, srText }) => (
    <button
        type="button"
        className={`${enabled ? 'bg-gray-800' : 'bg-gray-300'} relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500`}
        onClick={onChange}
        aria-checked={enabled}
        role="switch"
    >
        <span className="sr-only">{srText}</span>
        <span className={`${enabled ? 'translate-x-6' : 'translate-x-1'} inline-block w-4 h-4 transform bg-white rounded-full transition-transform`} />
    </button>
);


const AdminJournalPage: React.FC = () => {
    const { data: articles, setData: setArticles, isLoading } = useCollection<Article>('articles', { field: 'publishDate', direction: 'desc' });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [articleToDelete, setArticleToDelete] = useState<Article | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { showToast } = useToast();

    const openDeleteModal = (article: Article) => {
        setArticleToDelete(article);
        setIsModalOpen(true);
    };

    const handleDelete = async () => {
        if (!articleToDelete) return;
        
        setIsDeleting(true);
        try {
            await deleteDoc(doc(db, 'articles', articleToDelete.id));
            setArticles(prev => prev.filter(a => a.id !== articleToDelete.id));
            showToast('Article deleted successfully.', { type: 'success' });
        } catch (error) {
            console.error("Failed to delete article:", error);
            showToast("Error: Could not delete the article.", { type: 'error' });
        } finally {
            setIsModalOpen(false);
            setArticleToDelete(null);
            setIsDeleting(false);
        }
    };
    
    const handleTogglePublished = async (article: Article) => {
        const updatedArticle = { ...article, published: !article.published };
        const articleRef = doc(db, 'articles', article.id);
        await updateDoc(articleRef, { published: updatedArticle.published });
        setArticles(prev => prev.map(a => a.id === article.id ? updatedArticle : a));
    }

    return (
        <div>
            <ConfirmModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={handleDelete}
                title="Delete Article"
                description={`Are you sure you want to delete the article "${articleToDelete?.title}"? This action cannot be undone.`}
                isConfirming={isDeleting}
            />
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-gray-800">Manage Journal</h2>
                <Link to="/admin/article/new" className="inline-flex items-center px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors">
                   <PlusIcon className="h-5 w-5 mr-2"/>
                    Add New Article
                </Link>
            </div>
            <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                {isLoading && <p className="p-6 text-center">Loading articles...</p>}
                {!isLoading && (
                <table className="min-w-full leading-normal">
                    <thead>
                        <tr>
                            <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Article Title
                            </th>
                            <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Author
                            </th>
                             <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Publish Date
                            </th>
                             <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Published
                            </th>
                            <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                       {(articles || []).map(article => (
                            <tr key={article.id}>
                                <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                                    <Link to={`/admin/article/edit/${article.id}`} className="text-gray-900 hover:text-indigo-600 whitespace-no-wrap font-semibold">
                                        {article.title}
                                    </Link>
                                </td>
                                <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                                    <p className="text-gray-900 whitespace-no-wrap">{article.author.name}</p>
                                </td>
                                <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                                    <p className="text-gray-900 whitespace-no-wrap">
                                        {new Date(article.publishDate).toLocaleDateString()}
                                    </p>
                                </td>
                                <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm text-center">
                                    <ToggleSwitch enabled={!!article.published} onChange={() => handleTogglePublished(article)} srText={`Publish status for ${article.title}`} />
                                </td>
                                <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm text-right whitespace-no-wrap">
                                    <Link to={`/admin/article/edit/${article.id}`} className="text-indigo-600 hover:text-indigo-900 mr-4">
                                        Edit
                                    </Link>
                                    <button onClick={() => openDeleteModal(article)} className="text-red-600 hover:text-red-900">
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                )}
                 {!isLoading && articles && articles.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                        <p>No journal articles found.</p>
                        <p className="mt-2">
                            <Link to="/admin/article/new" className="text-indigo-600 hover:underline">
                                Create your first one!
                            </Link>
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminJournalPage;